// Smart Daara - Quiz Editor Page
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { quizAPI, courseAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { 
  ArrowLeft, Save, Plus, Pencil, Trash2, FileQuestion, CheckCircle,
  Loader2, GripVertical, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { buildTenantPath } from '../../lib/routing';

export default function QuizEditor() {
  const { courseId, quizId } = useParams();
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isNew = !quizId || quizId === 'new';
  const p = (sub) => buildTenantPath(user?.tenant_slug, sub);
  const base = user?.role === 'teacher' ? 'teacher' : 'school';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [course, setCourse] = useState(null);
  
  // Quiz data
  const [quiz, setQuiz] = useState({
    title: '',
    title_fr: '',
    description: '',
    description_fr: '',
    order: 1,
    passing_score: 80,
    questions_per_attempt: 10
  });

  // Questions
  const [questions, setQuestions] = useState([]);
  const [questionModalOpen, setQuestionModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question: '',
    question_fr: '',
    option_a: '',
    option_a_fr: '',
    option_b: '',
    option_b_fr: '',
    option_c: '',
    option_c_fr: '',
    option_d: '',
    option_d_fr: '',
    correct_answer: 'a'
  });

  useEffect(() => {
    loadData();
  }, [courseId, quizId]);

  const loadData = async () => {
    try {
      // Load course
      const courseRes = await courseAPI.getCourse(courseId);
      setCourse(courseRes.data);

      if (!isNew) {
        // Load quiz
        const quizRes = await quizAPI.getQuiz(quizId);
        setQuiz(quizRes.data);

        // Load questions
        const questionsRes = await quizAPI.listQuestions(quizId);
        setQuestions(questionsRes.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuizChange = (field, value) => {
    setQuiz(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveQuiz = async () => {
    if (!quiz.title_fr) {
      toast.error(language === 'fr' ? 'Le titre est requis' : 'Title is required');
      return;
    }

    setSaving(true);
    try {
      if (isNew) {
        const res = await quizAPI.createQuiz({
          ...quiz,
          course_id: courseId
        });
        toast.success(language === 'fr' ? 'Devoir créé!' : 'Quiz created!');
        navigate(p(`${base}/courses/${courseId}/quiz/${res.data.id}`), { replace: true });
      } else {
        await quizAPI.updateQuiz(quizId, quiz);
        toast.success(language === 'fr' ? 'Devoir mis à jour!' : 'Quiz updated!');
      }
    } catch (error) {
      console.error('Error saving quiz:', error);
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const openQuestionModal = (question = null) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionForm({
        question: question.question || '',
        question_fr: question.question_fr || '',
        option_a: question.option_a || '',
        option_a_fr: question.option_a_fr || '',
        option_b: question.option_b || '',
        option_b_fr: question.option_b_fr || '',
        option_c: question.option_c || '',
        option_c_fr: question.option_c_fr || '',
        option_d: question.option_d || '',
        option_d_fr: question.option_d_fr || '',
        correct_answer: question.correct_answer || 'a'
      });
    } else {
      setEditingQuestion(null);
      setQuestionForm({
        question: '',
        question_fr: '',
        option_a: '',
        option_a_fr: '',
        option_b: '',
        option_b_fr: '',
        option_c: '',
        option_c_fr: '',
        option_d: '',
        option_d_fr: '',
        correct_answer: 'a'
      });
    }
    setQuestionModalOpen(true);
  };

  const handleSaveQuestion = async (continueAdding = false) => {
    if (!questionForm.question_fr) {
      toast.error(language === 'fr' ? 'La question est requise' : 'Question is required');
      return;
    }

    if (!questionForm.option_a_fr || !questionForm.option_b_fr || 
        !questionForm.option_c_fr || !questionForm.option_d_fr) {
      toast.error(language === 'fr' ? 'Les 4 options sont requises' : 'All 4 options are required');
      return;
    }

    setSaving(true);
    try {
      if (editingQuestion) {
        await quizAPI.updateQuestion(editingQuestion.id, questionForm);
        setQuestions(prev => prev.map(q => 
          q.id === editingQuestion.id ? { ...q, ...questionForm } : q
        ));
        toast.success(language === 'fr' ? 'Question mise à jour!' : 'Question updated!');
        setQuestionModalOpen(false);
      } else {
        const res = await quizAPI.addQuestion(quizId, questionForm);
        setQuestions(prev => [...prev, res.data]);
        toast.success(language === 'fr' ? `Question ${questions.length + 1} ajoutée!` : `Question ${questions.length + 1} added!`);
        
        if (continueAdding) {
          // Reset form for next question
          setQuestionForm({
            question: '',
            question_fr: '',
            option_a: '',
            option_a_fr: '',
            option_b: '',
            option_b_fr: '',
            option_c: '',
            option_c_fr: '',
            option_d: '',
            option_d_fr: '',
            correct_answer: 'a'
          });
        } else {
          setQuestionModalOpen(false);
        }
      }
    } catch (error) {
      console.error('Error saving question:', error);
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteQuestion = async (question) => {
    if (!window.confirm(language === 'fr' 
      ? 'Êtes-vous sûr de vouloir supprimer cette question?' 
      : 'Are you sure you want to delete this question?')) {
      return;
    }

    try {
      await quizAPI.deleteQuestion(question.id);
      setQuestions(prev => prev.filter(q => q.id !== question.id));
      toast.success(language === 'fr' ? 'Question supprimée!' : 'Question deleted!');
    } catch (error) {
      console.error('Error deleting question:', error);
      toast.error(language === 'fr' ? 'Erreur de suppression' : 'Delete error');
    }
  };

  const getCorrectAnswerLabel = (answer) => {
    const labels = { a: 'A', b: 'B', c: 'C', d: 'D' };
    return labels[answer] || answer.toUpperCase();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="quiz-editor">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(p(`${base}/courses/${courseId}/edit`))}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-outfit font-bold text-slate-900">
              {isNew 
                ? (language === 'fr' ? 'Nouveau Devoir' : 'New Quiz')
                : (language === 'fr' ? 'Modifier le Devoir' : 'Edit Quiz')
              }
            </h1>
            <p className="text-slate-500">
              {course?.title_fr || course?.title}
            </p>
          </div>
        </div>
        <Button 
          onClick={handleSaveQuiz}
          disabled={saving}
          className="bg-emerald-700 hover:bg-emerald-800"
          data-testid="save-quiz-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {language === 'fr' ? 'Enregistrer' : 'Save'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Quiz Details */}
        <div className="lg:col-span-2 space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-outfit">
                {language === 'fr' ? 'Informations du Devoir' : 'Quiz Information'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Titre (Français)' : 'Title (French)'} *</Label>
                  <Input
                    value={quiz.title_fr}
                    onChange={(e) => handleQuizChange('title_fr', e.target.value)}
                    placeholder="Ex: Devoir 1 - Révision"
                    data-testid="quiz-title-fr"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Titre (Anglais)' : 'Title (English)'}</Label>
                  <Input
                    value={quiz.title}
                    onChange={(e) => handleQuizChange('title', e.target.value)}
                    placeholder="Ex: Quiz 1 - Review"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
                <Textarea
                  value={quiz.description_fr}
                  onChange={(e) => handleQuizChange('description_fr', e.target.value)}
                  rows={2}
                  placeholder={language === 'fr' ? 'Instructions pour les étudiants...' : 'Instructions for students...'}
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Position dans le cours' : 'Position in course'}</Label>
                  <Input
                    type="number"
                    min={1}
                    value={quiz.order}
                    onChange={(e) => handleQuizChange('order', parseInt(e.target.value) || 1)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Score requis (%)' : 'Required Score (%)'}</Label>
                  <Input
                    type="number"
                    min={1}
                    max={100}
                    value={quiz.passing_score}
                    onChange={(e) => handleQuizChange('passing_score', parseInt(e.target.value) || 80)}
                    data-testid="quiz-passing-score"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{language === 'fr' ? 'Questions par tentative' : 'Questions per attempt'}</Label>
                  <Input
                    type="number"
                    min={1}
                    value={quiz.questions_per_attempt}
                    onChange={(e) => handleQuizChange('questions_per_attempt', parseInt(e.target.value) || 10)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Questions Section */}
          {!isNew && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="font-outfit">
                    {language === 'fr' ? 'Questions' : 'Questions'}
                  </CardTitle>
                  <CardDescription>
                    {questions.length} {language === 'fr' ? 'question(s)' : 'question(s)'} - 
                    {quiz.questions_per_attempt} {language === 'fr' ? 'seront posées par tentative' : 'will be asked per attempt'}
                  </CardDescription>
                </div>
                <Button onClick={() => openQuestionModal()} data-testid="add-question-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  {language === 'fr' ? 'Ajouter' : 'Add'}
                </Button>
              </CardHeader>
              <CardContent>
                {questions.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <FileQuestion className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>{language === 'fr' ? 'Aucune question pour le moment' : 'No questions yet'}</p>
                    <p className="text-sm mt-1">
                      {language === 'fr' 
                        ? `Ajoutez au moins ${quiz.questions_per_attempt} questions`
                        : `Add at least ${quiz.questions_per_attempt} questions`
                      }
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {questions.map((question, index) => (
                      <div 
                        key={question.id}
                        className="p-4 bg-slate-50 rounded-lg"
                        data-testid={`question-item-${index}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-start gap-3">
                            <span className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center text-emerald-700 font-semibold text-sm flex-shrink-0">
                              {index + 1}
                            </span>
                            <p className="font-medium text-slate-900">
                              {question.question_fr || question.question}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => openQuestionModal(question)}
                            >
                              <Pencil className="w-4 h-4 text-slate-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => handleDeleteQuestion(question)}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </div>
                        
                        <div className="ml-11 grid grid-cols-2 gap-2 text-sm">
                          {['a', 'b', 'c', 'd'].map(opt => (
                            <div 
                              key={opt}
                              className={`p-2 rounded ${
                                question.correct_answer === opt 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : 'bg-white text-slate-600'
                              }`}
                            >
                              <span className="font-semibold">{opt.toUpperCase()}.</span>{' '}
                              {question[`option_${opt}_fr`] || question[`option_${opt}`]}
                              {question.correct_answer === opt && (
                                <CheckCircle className="w-3 h-3 inline ml-1" />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-outfit text-base">
                {language === 'fr' ? 'Paramètres' : 'Settings'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">{language === 'fr' ? 'Questions totales' : 'Total Questions'}</span>
                <span className="font-medium">{questions.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{language === 'fr' ? 'Par tentative' : 'Per Attempt'}</span>
                <span className="font-medium">{quiz.questions_per_attempt}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{language === 'fr' ? 'Score requis' : 'Required Score'}</span>
                <span className="font-medium">{quiz.passing_score}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">{language === 'fr' ? 'Bonnes réponses requises' : 'Correct Answers Required'}</span>
                <span className="font-medium text-emerald-700">
                  {Math.ceil(quiz.questions_per_attempt * quiz.passing_score / 100)}/{quiz.questions_per_attempt}
                </span>
              </div>
            </CardContent>
          </Card>

          {questions.length < quiz.questions_per_attempt && !isNew && (
            <Card className="border-0 shadow-sm bg-amber-50">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <p className="text-sm text-amber-800">
                    {language === 'fr' 
                      ? `Ajoutez au moins ${quiz.questions_per_attempt - questions.length} question(s) de plus pour que le devoir soit fonctionnel.`
                      : `Add at least ${quiz.questions_per_attempt - questions.length} more question(s) for the quiz to work.`
                    }
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Question Modal */}
      <Dialog open={questionModalOpen} onOpenChange={setQuestionModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingQuestion 
                ? (language === 'fr' ? 'Modifier la Question' : 'Edit Question')
                : (language === 'fr' ? 'Nouvelle Question' : 'New Question')
              }
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Question (Français)' : 'Question (French)'} *</Label>
              <Textarea
                value={questionForm.question_fr}
                onChange={(e) => setQuestionForm(prev => ({ ...prev, question_fr: e.target.value }))}
                rows={2}
                placeholder={language === 'fr' ? 'Posez votre question...' : 'Ask your question...'}
                data-testid="question-text"
              />
            </div>

            <div className="space-y-3">
              <Label>{language === 'fr' ? 'Options de réponse' : 'Answer Options'}</Label>
              
              {['a', 'b', 'c', 'd'].map(opt => (
                <div key={opt} className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                    questionForm.correct_answer === opt 
                      ? 'bg-emerald-600 text-white' 
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {opt.toUpperCase()}
                  </span>
                  <Input
                    value={questionForm[`option_${opt}_fr`]}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, [`option_${opt}_fr`]: e.target.value }))}
                    placeholder={`Option ${opt.toUpperCase()} *`}
                    className="flex-1"
                    data-testid={`option-${opt}`}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Bonne réponse' : 'Correct Answer'} *</Label>
              <RadioGroup 
                value={questionForm.correct_answer}
                onValueChange={(value) => setQuestionForm(prev => ({ ...prev, correct_answer: value }))}
                className="flex gap-4"
              >
                {['a', 'b', 'c', 'd'].map(opt => (
                  <div key={opt} className="flex items-center space-x-2">
                    <RadioGroupItem value={opt} id={`correct-${opt}`} />
                    <Label htmlFor={`correct-${opt}`} className="cursor-pointer">
                      {opt.toUpperCase()}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setQuestionModalOpen(false)}>
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </Button>
            {!editingQuestion && (
              <Button 
                onClick={() => handleSaveQuestion(true)}
                disabled={saving}
                variant="outline"
                className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                data-testid="save-continue-question-btn"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Plus className="w-4 h-4 mr-1" />
                {language === 'fr' ? 'Ajouter et continuer' : 'Add & Continue'}
              </Button>
            )}
            <Button 
              onClick={() => handleSaveQuestion(false)}
              disabled={saving}
              className="bg-emerald-700 hover:bg-emerald-800"
              data-testid="save-question-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingQuestion 
                ? (language === 'fr' ? 'Mettre à jour' : 'Update')
                : (language === 'fr' ? 'Ajouter et fermer' : 'Add & Close')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
